
import {
  Component,
  InjectReactive,
  Prop,
  Vue,
  Watch
} from "vue-property-decorator";
import CircleLoading from "@/components/loading/CircleLoading.vue";
import { PaginationFields } from "@/graphql/admin-fragments.ts";
import gql from "graphql-tag";
import { isDate } from "@/helpers/is-date";
import TimestampTdContent from "@/components/TimestampTdContent.vue";
import { ADMIN_PROFILE_SYMBOL } from "@/constants";
import SearchFilter from "@/components/admin/SearchFilter.vue";
import copyToClipboard from 'quasar/src/utils/copy-to-clipboard.js';;

// Mongo ObjectId, steamId64, uuid — такие значения удобно копировать кликом
const COPYABLE_REGEXP = /^(?:[a-f0-9]{24}|\d{17}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// Вкладка таблицы: своё условие поверх поиска (например, { appId: 730 })
interface AdminTableTab {
  name: string;
  label: string;
  icon?: string;
  img?: string;
  imgClass?: string;
  accent?: "primary" | "positive" | "warning";
  condition: Record<string, unknown>;
}

interface TableColumn {
  [key: string]: any;
  enum?: Array<any>;
  editable?: boolean;
  type: "number" | "string" | "boolean" | "json" | "image";
}

@Component({
  methods: { isDate },
  components: {
    SearchFilter,
    TimestampTdContent,
    CircleLoading
  }
})
export default class AdminSteamBot extends Vue {
  @Prop({}) title: any;
  @Prop({ required: true }) modelName: any;
  @Prop({ required: true }) columns: TableColumn[] | any;
  @Prop({ default: () => [] }) tabs!: AdminTableTab[];
  @InjectReactive(ADMIN_PROFILE_SYMBOL) public admin: any | null;
  JSON = JSON;

  pagination = {
    rowsPerPage: this.modelName === "steamBotModel" ? 50 : 1,
    page: 1,
    rowsNumber: 1,
    sortBy: null,
    descending: false
  };

  loading = false;
  storedSelectedRow = null;
  selected: any[] = [];
  docs: any[] = [];
  searchConditionText: any = "";
  PaginationFields = PaginationFields;
  pageHeight = 0;
  activeTab = "";
  tabCounts: Record<string, number> = {};

  created() {
    if (this.tabs.length) {
      // Вкладка живёт в адресе (?tab=dota2) — переживает перезагрузку
      const fromRoute = this.$route.query.tab;
      this.activeTab = this.tabs.some(tab => tab.name === fromRoute)
        ? (fromRoute as string)
        : this.tabs[0].name;
      this.loadTabCounts();
    }
  }

  get tabCondition() {
    const tab = this.tabs.find(tab => tab.name === this.activeTab);
    return tab ? tab.condition : {};
  }

  // Условие запроса: поиск/фильтр + условие вкладки (вкладка важнее)
  get queryCondition() {
    return { ...this.searchCondition, ...this.tabCondition };
  }

  selectTab(name: string) {
    if (name === this.activeTab) {
      return;
    }
    this.activeTab = name;
    this.pagination.page = 1;
    this.selected = [];
    this.$router
      .replace({ query: { ...this.$route.query, tab: name } })
      .catch(() => undefined);
  }

  // «Назад»/«Вперёд» и ссылка из дровера без ?tab меняют адрес без пересоздания страницы
  @Watch("$route.query.tab")
  onRouteTabChange(tab) {
    if (!this.tabs.length) {
      return;
    }
    const name = this.tabs.some(item => item.name === tab)
      ? tab
      : this.tabs[0].name;
    if (name !== this.activeTab) {
      this.activeTab = name;
      this.pagination.page = 1;
      this.selected = [];
    }
  }

  // Счётчики на вкладках — одним запросом, по документу на вкладку
  async loadTabCounts() {
    if (!this.tabs.length) {
      return;
    }

    const variables: Record<string, unknown> = {
      modelName: this.modelName,
      opts: { limit: 1, select: "_id" }
    };
    this.tabs.forEach((tab, i) => (variables[`c${i}`] = tab.condition));

    const args = this.tabs.map((tab, i) => `$c${i}: JSON`).join(", ");
    const fields = this.tabs
      .map(
        (tab, i) =>
          `t${i}: getModelDocs(modelName: $modelName, condition: $c${i}, opts: $opts) { totalDocs }`
      )
      .join("\n");

    try {
      const { data } = await this.$apollo.query({
        query: gql(
          `query adminTableTabCounts($modelName: String!, $opts: JSON, ${args}) { ${fields} }`
        ),
        variables,
        fetchPolicy: "no-cache"
      });
      this.tabCounts = this.tabs.reduce(
        (counts, tab, i) => ({
          ...counts,
          [tab.name]: data[`t${i}`].totalDocs
        }),
        {}
      );
    } catch (e) {
      // Счётчики — подсказка; без них вкладки работают
      this.tabCounts = {};
    }
  }

  // Отступ таблицы-карточки от краев страницы (= padding у #admin-table в стилях)
  get tableMargin() {
    return this.$q.screen.lt.md ? 8 : 16;
  }

  // «1–25 из 1 873»
  get pageRange() {
    const { page, rowsPerPage, rowsNumber } = this.pagination;
    if (!rowsNumber) {
      return "";
    }
    const from = (page - 1) * rowsPerPage + 1;
    const to = Math.min(page * rowsPerPage, rowsNumber);
    return `${from}–${to} из ${this.formatCount(rowsNumber)}`;
  }

  formatCount(value: number) {
    return Number(value).toLocaleString("ru-RU");
  }

  // Выбранные значения колонки-массива одной строкой: «Remoove, positive.bet»
  // (вместо чипов друг под другом — колонка не разрастается)
  arrayDisplayValue(col, values) {
    if (!Array.isArray(values) || !values.length) {
      return "—";
    }

    const labelOf = value => {
      const option = (col.enum || []).find(
        option => (col.optionValue ? option[col.optionValue] : option) === value
      );
      if (!option) {
        return value;
      }
      return col.optionLabel ? option[col.optionLabel] : option;
    };

    return values.map(labelOf).join(", ");
  }

  isCopyable(value) {
    return typeof value === "string" && COPYABLE_REGEXP.test(value);
  }

  copyValue(value: string) {
    copyToClipboard(value)
      .then(() =>
        this.$q.notify({
          message: `Скопировано: ${value}`,
          type: "positive",
          timeout: 1200
        })
      )
      .catch(() =>
        this.$q.notify({ message: "Не удалось скопировать", type: "negative" })
      );
  }

  @Watch("selected")
  selectedWatcher(value) {
    this.$emit("update:selected", value);
  }

  handleSelection({ rows, added, evt }) {
    // ignore selection change from header of not from a direct click event
    if (rows.length !== 1 || evt === void 0) {
      return;
    }

    const oldSelectedRow = this.storedSelectedRow;
    const [newSelectedRow] = rows;
    const { ctrlKey, shiftKey } = evt;

    if (shiftKey !== true) {
      this.storedSelectedRow = newSelectedRow;
    }
    // wait for the default selection to be performed
    this.$nextTick(() => {
      if (shiftKey === true) {
        const tableRows = this.$refs.tableRef.filteredSortedRows;

        let firstIndex = tableRows.indexOf(oldSelectedRow);
        let lastIndex = tableRows.indexOf(newSelectedRow);

        if (firstIndex < 0) {
          firstIndex = 0;
        }

        if (firstIndex > lastIndex) {
          [firstIndex, lastIndex] = [lastIndex, firstIndex];
        }

        const rangeRows = tableRows.slice(firstIndex, lastIndex + 1);
        // we need the original row object so we can match them against the rows in range
        const selectedRows = this.selected;

        this.selected =
          added === true
            ? selectedRows.concat(
                rangeRows.filter(row => !selectedRows.includes(row))
              )
            : selectedRows.filter(row => rangeRows.includes(row) === false);
      } else if (ctrlKey !== true && added === true) {
      }
    });
  }

  styleFn(offset, height) {
    this.pageHeight = height - offset;

    return {
      height: height - offset + "px",
      "overflow-x": "hidden"
    };
  }

  deselectAll() {
    this.selected = [];
  }

  selectAll() {
    this.selected = this.docs;
  }

  async updateMutate({ variables = {} }) {
    this.loading = true;
    try {
      await this.$apollo.mutate({
        mutation: gql`
          mutation updateModelDoc(
            $_id: ID!
            $updatedFields: JSON!
            $unsetFields: JSON
            $modelName: String!
          ) {
            updateModelDoc(
              _id: $_id
              updatedFields: $updatedFields
              modelName: $modelName
              unsetFields: $unsetFields
            )
          }
        `,
        variables: { ...variables }
      });
    } catch (e) {
      this.$q.notify({
        message: e.message,
        multiLine: true,
        type: "negative"
      });
    }
    this.loading = false;
  }

  search(condition) {
    this.searchConditionText = condition ? JSON.stringify(condition) : "";
  }

  applyFilterCondition(condition) {
    this.pagination.page = 1;
    this.search(Object.keys(condition || {}).length ? condition : null);
  }

  // Слоты верха: condition — текущее условие поиска, filter — заменить его
  // (с первой страницы), чтобы кнопки страницы дополняли условие, а не стирали
  slotProps(query) {
    return {
      query,
      search: this.search,
      condition: this.searchCondition,
      filter: this.applyFilterCondition
    };
  }

  get searchCondition() {
    if (this.searchConditionText.indexOf("{") === 0) {
      let condition;
      try {
        eval(`condition=${this.searchConditionText}`);
      } catch {}
      return condition ? condition : {};
    }
    return this.searchConditionText ? { _id: this.searchConditionText } : {};
  }

  get pagesNumber() {
    return Math.ceil(this.pagination.rowsNumber / this.pagination.rowsPerPage);
  }

  get opts() {
    const limit =
      this.modelName === "steamBotModel"
        ? { limit: this.pagination.rowsPerPage || this.pagination.rowsNumber }
        : {};
    return this.pagination.sortBy
      ? {
          ...limit,
          sort: {
            [this.pagination.sortBy]: this.pagination.descending ? -1 : 1
          }
        }
      : limit;
  }

  onQueryResult({ data }) {
    if (data && data.getModelDocs) {
      if (data.getModelDocs.page > data.getModelDocs.totalPages) {
        this.pagination.page = data.getModelDocs.totalPages;
      }

      this.pagination.rowsPerPage = data.getModelDocs.limit;

      this.pagination.rowsNumber = data.getModelDocs.totalDocs;
      this.docs = data.getModelDocs.docs.slice();
      // Чтобы страница могла догрузить связанные данные для текущих строк
      this.$emit("update:docs", this.docs);
    }
  }

  refetchQuery() {
    this.$refs.query["$apollo"].queries.query.refetch();
    this.loadTabCounts();
  }
}
